import type { IProperty } from 'src/services/api/properties/types';
import {
    formatPropertyAddress,
    formatRent,
    hasBedBathCounts,
} from 'src/components/property-details-card/property-detail-rows';

/**
 * A short, sendable blurb about a property, for dropping into the lead conversation
 * when the assigned unit is not a fit and the agent wants to offer an alternative.
 *
 * Written for SMS: one paragraph, no markdown, links as bare URLs. The agent is
 * expected to edit it before sending, so it favours completeness over voice.
 */
export const buildPropertyPitch = (property: IProperty): string => {
    const specs: string[] = [];
    if (hasBedBathCounts(property)) {
        specs.push(`${property.num_bedrooms} bed / ${property.num_bathrooms} bath`);
    }
    if (property.type) {
        specs.push(property.type.toLowerCase());
    }
    const rent = formatRent(property.rent);
    if (rent) {
        specs.push(rent);
    }

    const address = formatPropertyAddress(property);
    /*
     * Most properties are named after their street address, which would read as
     * "5171 Velma Dr at 5171 Velma Dr, Fort Worth…" — mention the name only when it
     * adds something the address does not.
     */
    const place = address.toLowerCase().startsWith(property.name.toLowerCase()) ?
        address :
        `${property.name} at ${address}`;

    const sentences = [
        `Another option you might like: ${place}${specs.length > 0 ? ` — ${specs.join(', ')}` : ''}.`,
    ];

    if (property.showing_scheduling_link) {
        sentences.push(`You can schedule a tour here: ${property.showing_scheduling_link.link}`);
    }
    if (property.application_link) {
        sentences.push(`Apply here: ${property.application_link.link}`);
    }

    return sentences.join(' ');
};
